import { useSelector } from 'react-redux';
import {
  getFirstParentForDataset,
  getMyAvailableDatasetEntities,
  getMyDataSetEntities,
} from 'js/reducers/selectors';
import { useMemo } from 'react';
import { isAdminAccess, isManagedResource, isReadOnly } from 'js/lib/ACLUtils';
import has from 'lodash/has';
import { getConnectionOrVendorType, isDetectedDataset } from 'js/lib/ResourceUtils';
import UIUtils from 'js/lib/UIUtils';
import { Nexset } from 'new/entities/Nexset';
import isNil from 'lodash/isNil';
import { applyFilters } from 'js/lib/SearchFilterUtils';
import { SearchFilterViews } from 'js/constants/visibilityFilters';

const getSourceMetaFromDataSet = (dataSetId, isDetected, state) => {
  //check if it is source/sink
  const {
    entities: { dataSets, sources, vendors },
    paging: { metaInfoById },
  } = state;
  const dataSet = dataSets[dataSetId];
  if (isDetected === false) {
    return null;
  } else if (isNil(isDetected) && isDetectedDataset(dataSet) === false) {
    return null;
  }

  const sourceId = dataSet.data_source;
  const sourceEntity = sources[sourceId];

  let connectionOrVendorType = getConnectionOrVendorType(
    sourceEntity,
    vendors,
    'source_type',
    'vendor_id',
  );

  return connectionOrVendorType && has(metaInfoById, connectionOrVendorType)
    ? metaInfoById[connectionOrVendorType]
    : null;
};

type Options = {
  userId?: number;
  orgId?: number;
  shared: 'to' | 'from';
};

export function prepareNexset(nexset, state) {
  const { entities, dataSets } = state;
  const { users, vendors, sources, sinks, docs } = entities;
  const { summaryById, docById } = dataSets;
  const readOnly = isReadOnly(nexset);
  const isManaged = isManagedResource(nexset);
  const adminAccess = isAdminAccess(nexset);
  let connectionOrVendorType = null;

  const owner = users[nexset.owner];

  const isDetected = isDetectedDataset(nexset);
  let source = null,
    parentDataSet = null;

  if (nexset.id) {
    connectionOrVendorType = getConnectionOrVendorType(
      nexset,
      vendors,
      'credentials_type',
      'vendor_id',
    );
  }

  let sourceIcon, sourceName;
  if (isDetected === true) {
    source = nexset ? sources[nexset.data_source] : null;
    const connectionInfo = getSourceMetaFromDataSet(nexset.id, isDetected, state);
    if (connectionInfo) {
      sourceIcon = connectionInfo?.iconSm;
      sourceName = connectionInfo?.name;
    } else {
      sourceName = connectionOrVendorType || '';
    }
  } else {
    // @ts-ignore
    parentDataSet = getFirstParentForDataset(state, { cardData: nexset });
  }

  const summaryData = nexset && has(summaryById, nexset.id) ? summaryById[nexset.id] : null;

  const dataSinks = nexset?.data_sinks?.map((sink) => sinks[sink]);

  return {
    ...nexset,
    ...summaryData,
    readOnly,
    isManaged,
    adminAccess,
    owner,
    isDetected,
    source: {
      ...source,
      ...connectionOrVendorType,
    },
    parentDataSet,
    sourceIcon,
    sourceName,
    doc: docs?.[docById?.[nexset?.id]]?.text,
    dataSinks,
    data_sinks: dataSinks,
    transformedCount: summaryData?.data_sets?.total || 0,
    destinationCount: nexset?.data_sinks?.length || 0,
    sharesCount: UIUtils.getDatasetSharersCount(nexset, summaryData) || 0,
  };
}

export function useMyNexsets(options?: Options): Nexset[] {
  const state = useSelector((state: any) => state);
  const {
    entities: { sources, users, vendors, dataSets, sinks, docs },
    dataSets: { summaryById, docById },
    app: {
      visibilityFilters: { filters },
    },
    dataSetSchemas,
  } = state;
  const allNexsets = getMyDataSetEntities(state);
  const availableNexsets = getMyAvailableDatasetEntities(state);
  return useMemo(() => {
    let nexsets = Object.values({
      ...allNexsets,
      ...availableNexsets,
    });

    const { filteredObjects } = applyFilters(
      nexsets,
      filters[SearchFilterViews.MY_DATA_SETS],
      dataSets,
      dataSetSchemas,
    );

    nexsets = filteredObjects as Nexset[];

    const mappedNexsets = nexsets.map((nexset) => prepareNexset(nexset, state));
    if (!options || (!options.orgId && !options.userId)) {
      return mappedNexsets;
    }
    return mappedNexsets.filter((nexset) => {
      if (options.shared === 'from') {
        if (options.orgId) {
          return (
            (nexset.org_id || nexset.org) == options.orgId &&
            nexset.access_roles.length > 0 &&
            nexset.access_roles[0] !== 'owner'
          );
        }
        if (options.userId) {
          return (
            (nexset.owner_id || nexset.owner) == options.userId &&
            nexset.access_roles.length > 0 &&
            nexset.access_roles[0] !== 'owner'
          );
        }
      }
      return false;
    });
  }, [
    allNexsets,
    availableNexsets,
    sinks,
    dataSets,
    docs,
    docById,
    sources,
    users,
    vendors,
    summaryById,
    options?.orgId,
    options?.userId,
    options?.shared,
  ]);
}
